/** @type {import('tailwindcss').Config} */
const defaultTheme = require('tailwindcss/defaultTheme');
const colorTokens = require('./tailwind.tokens.js');

const toChannels = (hex) => {
  const value = parseInt(hex.slice(1), 16);
  return `${(value >> 16) & 255} ${(value >> 8) & 255} ${value & 255}`;
};

const tokenColor = (name) => `rgb(var(--color-${name}) / <alpha-value>)`;

const toCssVariables = (values) =>
  Object.fromEntries(
    Object.entries(values).map(([name, value]) => [
      `--color-${name}`,
      value.startsWith('#') ? toChannels(value) : value,
    ])
  );

const lightTheme = {
  ...toCssVariables(colorTokens.light),
  colorScheme: 'light',
};
const darkTheme = toCssVariables(colorTokens.dark);

module.exports = {
  darkMode: ['variant', '&:is(.dark *):not(.light, .light *)'],
  safelist: ['dark'],
  theme: {
    screens: {
      md: '768px',
      lg: '1024px',
      xl: '1240px',
      '2xl': '1536px',
    },
    colors: {
      transparent: 'transparent',
      current: 'currentColor',
      black: '#000000',
      white: '#FFFFFF',
      success: {
        DEFAULT: '#3AC561',
        700: '#007f52',
        800: '#007D3A',
      },
      error: '#E12727',
      green: {
        200: '#E8F5EB',
        300: '#ddeae5',
        500: '#007f52',
        600: '#027D3B',
        700: '#025E2C',
      },
      orange: {
        DEFAULT: '#E57C00',
        'sales-soon': '#D67200',
      },
      blue: {
        100: '#E9F5FE',
        200: '#D3EAFD',
        300: '#BDE0FC',
        400: '#A6D5FA',
        500: '#90CAF9',
        600: '#7AC0F8',
        700: '#64B6F7',
        DEFAULT: '#3A5BC7',
        800: '#4DABF5',
        900: '#37A0F4',
        hover: '#4268E6',
        dark: '#9EB1FF',
        1000: '#3B5BC7',
      },
      grey: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#E6E6E6',
        300: '#CCCCCC',
        400: '#A6A6A6',
        450: '#999999',
        500: '#808080',
        600: '#666666',
        700: '#4C4C4C',
        800: '#262626',
        850: '#242424',
        900: '#141414',
      },
      pink: {
        200: '# FEFBE9',
        500: '#D67202',
      },
      gold: {
        DEFAULT: '#ff9800',
      },
      red: {
        100: '#FFF7F7',
        200: '#FBEAEA',
        400: '#FF4848',
        500: '#E12727',
        600: '#D3171E',
        700: '#D3161F',
      },
      yellow: {
        200: '#FDF2C0',
        300: '#FEFBE9',
        500: '#EAB308',
        600: '#C6A700',
        700: '#D67202',
        800: '#AB6400',
      },
      canvas: tokenColor('canvas'),
      surface: {
        DEFAULT: tokenColor('surface'),
        muted: tokenColor('surface-muted'),
        strong: tokenColor('surface-strong'),
        raised: tokenColor('surface-raised'),
        inverse: tokenColor('surface-inverse'),
      },
      content: {
        DEFAULT: tokenColor('content'),
        muted: tokenColor('content-muted'),
        subtle: tokenColor('content-subtle'),
        inverse: tokenColor('content-inverse'),
      },
      line: {
        DEFAULT: tokenColor('line'),
        medium: tokenColor('line-medium'),
        strong: tokenColor('line-strong'),
      },
      accent: {
        DEFAULT: tokenColor('accent'),
        hover: tokenColor('accent-hover'),
        content: tokenColor('accent-content'),
      },
      link: {
        DEFAULT: tokenColor('link'),
        hover: tokenColor('link-hover'),
      },
      focus: tokenColor('focus'),
      danger: {
        DEFAULT: tokenColor('danger'),
        surface: tokenColor('danger-surface'),
      },
      positive: {
        DEFAULT: tokenColor('positive'),
        surface: tokenColor('positive-surface'),
      },
      warning: {
        DEFAULT: tokenColor('warning'),
        surface: tokenColor('warning-surface'),
      },
      overlay: 'var(--color-overlay)',
    },
    container: {
      center: true,
      padding: {
        DEFAULT: `1rem`,
        sm: '0.5rem',
        md: '2rem',
        lg: '2rem',
        xl: '0rem',
      },
    },
    fontFamily: {
      sans: ['Inter', ...defaultTheme.fontFamily.sans],
    },
    fontSize: {
      xxs: ['0.65rem', { lineHeight: '0.75rem' }],
      xs: ['0.75rem', { lineHeight: '1.25rem' }],
      sm: ['0.875rem', { lineHeight: '1.25rem' }],
      base: ['1rem', { lineHeight: '1.375rem' }],
      md: ['1.125rem', { lineHeight: '1.5rem' }],
      lg: ['1.25rem', { lineHeight: '1.6rem' }],
      xl: ['1.5rem', { lineHeight: '2rem' }],
      '2xl': ['1.6rem', { lineHeight: '2rem' }],
      '3xl': ['1.75rem', { lineHeight: '2rem' }],
      '4xl': ['2rem', { lineHeight: '2.25rem' }],
      '5xl': ['2.125rem', { lineHeight: '2.375rem' }],
      '6xl': ['2.25rem', { lineHeight: '2.5rem' }],
      '7xl': ['2.75rem', { lineHeight: '3rem' }],
    },
    letterSpacing: {
      tighter: '-0.006em',
      tight: '-0.003em',
      normal: '0em',
      medium: '0.14em',
      wide: '0.18em',
      wider: '0.2em',
    },
    extend: {
      aspectRatio: {
        '3/2': '3 / 2',
      },
      backgroundImage: {
        'outline-btn-hover':
          'linear-gradient(to right, #4268E6 50%, transparent 50%)',
        'outline-btn-hover-dark':
          'linear-gradient(to right, #FFFFFF 50%, transparent 50%)',
        'outline-btn-hover-light':
          'linear-gradient(to right, #000000 50%, transparent 50%)',
      },
      backgroundSize: {
        '200-100': 'calc(200% + 4px) 100%',
      },
      lineHeight: {
        7: '1.725rem',
      },
      spacing: {
        18: '4.375rem',
        110: '25rem',
        112: '28rem',
        116: '32rem',
      },
      transitionDuration: {
        120: '120ms',
      },
      transitionProperty: {
        color: 'color',
        margin: 'margin',
      },
      boxShadow: {
        card: '0px 8px 60px -8px rgba(0,0,0,0.16);',
        cardDark: '0px 12px 44px 0px rgba(33,150,243,0.4);',
      },
      scale: {
        143: '1.43',
      },
      maxWidth: {
        'search-bar': '800px',
        'search-xl': '1292px',
        'search-2xl': '1732px',
      },
      borderRadius: {
        thumbs: '4px',
      },
      keyframes: {
        slideIn: {
          from: {
            transform: 'translateX(100%)',
            opacity: 0,
          },
          to: {
            transform: 'translateX(0)',
            opacity: 1,
          },
        },
        slideOut: {
          '0%': { transform: 'translateX(0)' },
          '100%': { transform: 'translateX(100%)' },
        },
        slideDown: {
          from: { height: '0', opacity: 0 },
          to: { height: 'var(--radix-accordion-content-height)', opacity: 1 },
        },
        slideUp: {
          from: { height: 'var(--radix-accordion-content-height)', opacity: 1 },
          to: { height: '0', opacity: 0 },
        },
        overlayShow: {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        overlayHide: {
          '0%': { opacity: 1 },
          '100%': { opacity: 0 },
        },
      },
      animation: {
        slideIn: 'slideIn 500ms cubic-bezier(0.16, 1, 0.3, 1)',
        slideOut: 'slideOut 300ms ease-in-out',
        overlayShow: 'overlayShow 300ms cubic-bezier(0.16, 1, 0.3, 1)',
        overlayHide: 'overlayHide 300ms ease-in-out',
        slideDown: 'slideDown 280ms cubic-bezier(0.2, 0, 0, 1)',
        slideUp: 'slideUp 220ms cubic-bezier(0.4, 0, 1, 1)',
      },
    },
  },
  plugins: [
    function ({ addBase }) {
      addBase({
        ':root': toCssVariables(colorTokens.light),
        '.dark': darkTheme,
        ':root.dark': { colorScheme: 'dark' },
        '.light': lightTheme,
      });
    },
    function ({ addVariant }) {
      addVariant('children', '& > *');
    },
    function ({ addComponents }) {
      addComponents({
        // We want container to be fluid at all screen widths except xl
        // need to override since by default container's max width is set to width of screen breakpoint
        '.container': {
          maxWidth: '100%',
          '@screen xl': {
            maxWidth: '1230px',
          },
        },
      });
    },
  ],
};
